/**
 * Auth — Entra sign-in for internal first-party members.
 * Per Shipping-Module-Architecture-v1.md §4.
 *
 * Strategy (mirrors the Reports portal kit's src/auth/index.ts):
 *  - PublicClientApplication wraps the app via MsalProvider (in main.tsx).
 *  - All app routes gated by <AuthRoute> using msal-react's useIsAuthenticated.
 *  - In dev mode (no VITE_ENTRA_CLIENT_ID) a bypass returns a stub Admin user so
 *    the UI loads without an Azure app registration.
 *  - In prod, the user's App Roles come from the validated bearer's `roles[]`
 *    claim (architecture §4.1/§4.2). /api/auth/me projects that claim; until the
 *    real API exists, roles are read from the MSAL id-token claims (see
 *    RoleContext) so the role gating is exercisable against a live Entra app.
 *
 * CONTRAST with the Reports portal (architecture §4.3): there is NO HMAC
 * signed-link cookie path and NO B2B-guest flow here. Warehouse and Admin are
 * internal @claridynamics.com members; Entra sign-in is the only gate. The app
 * registration sets appRoleAssignmentRequired=true (a third gate) so only
 * assigned users can sign in at all.
 */
import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import type { ShippingRole, ShippingUser } from '@/types/index';

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || 'dev-mode-client-id',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'common'}`,
    redirectUri: `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: `${window.location.origin}/login`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Per architecture §7.5 PHI cache discipline: tab-scoped sessionStorage —
    // tokens never hit disk and clear on tab close. storeAuthStateInCookie keeps
    // only the short-lived nonce/state for silent renewal, never tokens. This is
    // the exact pairing the Reports kit uses (src/auth/index.ts).
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    iframeHashTimeout: 6_000,
    loadFrameTimeout: 10_000,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize once at module load — required by msal-browser v3.
let initPromise: Promise<void> | null = null;
export async function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = msalInstance.initialize();
  }
  return initPromise;
}

/**
 * Login scopes — the minimum needed at sign-in. The App Roles arrive in the
 * id-token `roles[]` claim once the app registration defines them and the user
 * is assigned via sg-shipping-warehouse / sg-shipping-admin (architecture §4.1).
 */
export const loginRequest = {
  scopes: ['User.Read', 'profile', 'openid', 'email'],
};

/**
 * API token request — the scope used to mint the Entra access token that the API
 * choke-point validates and reads `claims.roles` from (architecture §4.2). The
 * client id is a placeholder until the "CDX Shipping" app registration is created
 * and its access_as_user scope is exposed; wire the real GUID via
 * VITE_ENTRA_API_CLIENT_ID at deploy time.
 */
export const apiTokenRequest = {
  scopes: [
    `api://${import.meta.env.VITE_ENTRA_API_CLIENT_ID || 'cdx-shipping-api'}/access_as_user`,
  ],
};

/** Stub user for dev-mode bypass (when not connected to real Entra). */
export const DEV_USER: ShippingUser = {
  userId: 'dev-user-00000',
  email: 'dev@claridynamics.local',
  displayName: 'Dev User (Warehouse + Admin)',
  // Dev bypass holds BOTH roles so every gated surface is reachable locally.
  roles: ['Warehouse', 'Admin'],
};

/** Returns true when we're in dev-mode auth bypass (no Entra app configured). */
export function isDevAuthBypass(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_ENTRA_CLIENT_ID;
}

/** The App Roles we recognize. Used to filter unknown values out of the claim. */
const KNOWN_ROLES: readonly ShippingRole[] = ['Warehouse', 'Admin'];

/**
 * Extract App Roles from a set of token claims. Entra puts App Role *values* in
 * the `roles` claim (architecture §4.1 — "lands in the token roles[] claim,
 * which jwt.ts already parses"). Unknown values are dropped so a future role
 * cannot accidentally unlock a gated surface in this build.
 */
export function rolesFromClaims(claims: Record<string, unknown> | undefined): ShippingRole[] {
  const raw = claims?.['roles'];
  if (!Array.isArray(raw)) return [];
  return raw.filter((r): r is ShippingRole =>
    typeof r === 'string' && (KNOWN_ROLES as readonly string[]).includes(r),
  );
}
